/**
 * The status pill floating over the top of the canvas: which document this is,
 * and the one or two things you can do to it.
 *
 * The draft is the only editable document, so a version offers Edit (copy it
 * into the draft) and — when it is not the live one — Publish (make it live
 * again). Both replace the draft, so both are confirmed first.
 */

import { Tooltip } from 'antd';
import React, { FC } from 'react';
import OWButton from '../ow_button/OWButton';
import { AOPVersionMode, AOP_VERSION_CHROME } from './utils/AOPVersionUtils';

interface AOPVersionPillProps {
	mode: AOPVersionMode;
	selectedVersion: number | null;
	publishedVersion: number | null;
	/** The draft holds something the live version does not. */
	/** Null while the draft cannot be compared with the live version yet. */
	hasUnpublishedChanges: boolean | null;
	/**
	 * Why the draft cannot be written, or null when it can.
	 *
	 * Publishing cuts a version from what the service holds and restoring
	 * replaces the draft, so both are refused while the service does not have
	 * the author's latest plan — a save still on its way, or one that failed.
	 *
	 * One field rather than a flag and a reason: a disabled control that does
	 * not say why is the reason somebody has to go and read the payload, and
	 * two fields could disagree about whether there was anything to say.
	 */
	writeBlockedReason: string | null;
	isPublishing: boolean;
	onPublish: () => void;
	onEdit: () => void;
}

export const AOPVersionPill: FC<AOPVersionPillProps> = ({
	mode,
	selectedVersion,
	publishedVersion,
	hasUnpublishedChanges,
	writeBlockedReason,
	isPublishing,
	onPublish,
	onEdit,
}) => {
	const chrome = AOP_VERSION_CHROME[mode];
	const name = mode === 'draft' ? 'DRAFT' : `V${selectedVersion}`;
	const hasPublished = publishedVersion !== null && publishedVersion !== undefined;

	// "Not published" is only true of a procedure that has never been published.
	// Once there is a live version the draft is either ahead of it or the same as
	// it, and saying "not published" would read as though nothing were live.
	const draftDescriptor = !hasPublished
		? 'Not published'
		: hasUnpublishedChanges === null
		? // The live version has not been read, so whether this matches it is not
		  // something the pill can say. It names the draft and stops there.
		  'Draft'
		: hasUnpublishedChanges
		? 'Unpublished changes'
		: `Same as v${publishedVersion}`;

	const descriptor =
		mode === 'draft'
			? draftDescriptor
			: mode === 'published'
			? 'Published version'
			: 'Previously published';

	// A draft that matches what is already live has nothing to add, which is a
	// different thing from a draft the service will not take.
	// `=== false` rather than `!`: not-yet-known is not the same as unchanged,
	// and treating it as such is how a draft came to be labelled live.
	const isUnchangedFromLive = mode === 'draft' && hasPublished && hasUnpublishedChanges === false;
	// Restoring replaces the draft, so Edit waits on the same thing Publish does.
	const canWrite = writeBlockedReason === null;
	const isPublishDisabled = !canWrite || isUnchangedFromLive;
	// The write block comes first: "nothing to publish" is about this draft
	// against the live version, and neither is the current document while a save
	// is outstanding.
	const blockedReason = !canWrite
		? writeBlockedReason
		: isUnchangedFromLive
		? `This draft is the same as v${publishedVersion}, so there is nothing to publish.`
		: null;

	/*
	  A disabled button takes no pointer events, so a tooltip on one has to hang
	  off a live element wrapped around it.
	*/
	const action = (
		label: string,
		disabled: boolean,
		reason: string | null,
		onClick: () => void,
		loading?: boolean
	) => {
		const control = (
			<OWButton
				size="small"
				ghost={chrome.onDark}
				className={`footnoteMedium uppercase tracking-wider ${chrome.button}`}
				disabled={disabled}
				loading={loading}
				onClick={onClick}
			>
				{label}
			</OWButton>
		);

		return disabled && reason ? (
			<Tooltip title={reason}>
				<span className="inline-flex">{control}</span>
			</Tooltip>
		) : (
			control
		);
	};

	return (
		<div
			// Square across the top and rounded underneath: it hangs from the
			// canvas's accent line rather than floating below it.
			className={`pointer-events-auto flex items-center gap-xs rounded-b px-s py-2xs shadow-md ${chrome.pill}`}
		>
			<span className={`footnoteMedium uppercase tracking-wider ${chrome.accentText}`}>{name}</span>
			<span className={`footnoteregular ${chrome.mutedText}`}>· {descriptor}</span>

			{mode === 'draft' ? null : action('Edit', !canWrite, writeBlockedReason, onEdit)}

			{mode === 'published'
				? null
				: action('Publish', isPublishDisabled, blockedReason, onPublish, isPublishing)}
		</div>
	);
};

export default AOPVersionPill;
